/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';

const formatStatNumber = (n) => {
  if (n == null || n === '') return n;
  if (typeof n === 'number' && Number.isFinite(n)) {
    return new Intl.NumberFormat('pt-BR').format(n);
  }
  return n;
};

/**
 * Card de métrica numérica genérico (v2).
 *
 * Props:
 *  - label       : texto acima do número (ex.: "Aguardando Validação")
 *  - value       : número a exibir; null/undefined mostra "—"
 *  - valueFontSize : tamanho da fonte do valor (ex.: '1.25rem', 20)
 *  - icon        : node React (ex.: <InfoIcon />)
 *  - color       : 'info' | 'success' | 'error' | 'warning' | 'primary' | 'default'
 *  - loading     : exibe skeleton no lugar do número
 *  - active      : destaca o card com borda e cor
 *  - activeLabel : texto extra exibido quando active=true
 *  - onClick     : torna o card clicável
 */

const PALETTE = {
  info:    { light: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
  success: { light: '#e8f5e9', border: '#388e3c', text: '#1b5e20' },
  error:   { light: '#ffebee', border: '#d32f2f', text: '#b71c1c' },
  warning: { light: '#fff3e0', border: '#f57c00', text: '#e65100' },
  primary: { light: '#e8eaf6', border: '#3f51b5', text: '#1a237e' },
  default: { light: '#f5f5f5', border: '#9e9e9e', text: '#424242' },
};

const StatCardV2 = ({
  label,
  value,
  valueFiltered,
  valueTotal,
  totalCaption,
  valueFontSize,
  icon,
  color,
  loading,
  active,
  activeLabel,
  hint,
  onClick,
}) => {
  const palette = PALETTE[color] || PALETTE.default;
  const isClickable = !!onClick;
  const hasSplitValue = valueTotal != null && valueTotal !== '';
  const displayValue = formatStatNumber(value);
  const displayFiltered = formatStatNumber(valueFiltered);
  const displayTotal = formatStatNumber(valueTotal);
  const valueTypographySx = {
    fontWeight: 700,
    color: active ? palette.text : 'text.primary',
    ...(valueFontSize != null && { fontSize: valueFontSize }),
  };

  const renderValue = () => {
    if (hasSplitValue) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75 }}>
          <Typography
            component="span"
            variant="h4"
            sx={{
              ...valueTypographySx,
              lineHeight: 1.2,
            }}
          >
            {displayFiltered ?? 0}
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: 500,
              color: active ? palette.text : 'text.secondary',
              lineHeight: 1.2,
              fontSize: '0.875rem',
            }}
          >
            {`de ${displayTotal ?? '—'} ${totalCaption}`}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography
        variant="h4"
        sx={{
          ...valueTypographySx,
          lineHeight: 1,
        }}
      >
        {displayValue ?? '—'}
      </Typography>
    );
  };

  const content = (
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
              fontSize: '0.65rem',
              lineHeight: 1.2,
              display: 'block',
            }}
          >
            {label}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {loading ? (
              <Skeleton width={80} height={36} sx={{ borderRadius: 1 }} />
            ) : (
              renderValue()
            )}
          </Box>
        </Box>
        {icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? `${palette.border}22`
                  : palette.light,
              flexShrink: 0,
            }}
          >
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { sx: { fontSize: 18, color: palette.text, ...icon.props?.sx } })
              : icon}
          </Box>
        )}
      </Box>
      {active && activeLabel && (
        <Typography
          variant="caption"
          sx={{ color: palette.text, fontWeight: 600, display: 'block', mt: 0.5 }}
        >
          {activeLabel}
        </Typography>
      )}
    </CardContent>
  );

  const card = (
    <Card
      variant="outlined"
      sx={{
        borderColor: active ? palette.border : 'divider',
        borderWidth: active ? 2 : 1,
        borderRadius: 2,
        transition: 'all 0.2s ease',
        boxShadow: active
          ? `0 0 0 2px ${palette.border}40`
          : (theme) => (theme.palette.mode === 'dark' ? 2 : 1),
        ...(isClickable && {
          '&:hover': {
            borderColor: palette.border,
            boxShadow: `0 0 0 2px ${palette.border}30`,
          },
        }),
      }}
    >
      {isClickable ? (
        <CardActionArea onClick={onClick} sx={{ borderRadius: 2 }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );

  if (hint) {
    return (
      <Tooltip title={hint} arrow>
        {card}
      </Tooltip>
    );
  }

  return card;
};

StatCardV2.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueFiltered: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalCaption: PropTypes.string,
  valueFontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.node,
  color: PropTypes.oneOf(['info', 'success', 'error', 'warning', 'primary', 'default']),
  loading: PropTypes.bool,
  active: PropTypes.bool,
  activeLabel: PropTypes.string,
  hint: PropTypes.string,
  onClick: PropTypes.func,
};

StatCardV2.defaultProps = {
  value: null,
  valueFiltered: null,
  valueTotal: null,
  totalCaption: 'no total',
  valueFontSize: null,
  icon: null,
  color: 'default',
  loading: false,
  active: false,
  activeLabel: 'Filtro ativo — clique para remover',
  hint: null,
  onClick: null,
};

export default StatCardV2;
